/**
 * Firestore job writes reject `undefined` anywhere in nested objects.
 * Use {@link sanitizeJobPayloadForFirestore} on payloads passed to addDoc/updateDoc for jobs.
 */
import { FieldValue, Timestamp } from "firebase/firestore";

function stripUndefinedDeep(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof FieldValue) return value;
  if (value instanceof Timestamp) return value;
  if (Array.isArray(value)) {
    return (value.map(stripUndefinedDeep) as unknown[]).filter((v) => v !== undefined);
  }
  if (typeof value === "object" && value !== null) {
    const proto = Object.getPrototypeOf(value);
    if (proto === Object.prototype) {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const next = stripUndefinedDeep(v);
        if (next !== undefined) out[k] = next;
      }
      return out;
    }
    return value;
  }
  return value;
}

/** Remove undefined recursively so Firestore accepts the payload. Preserves null and FieldValue/Timestamp. */
export function sanitizeJobPayloadForFirestore(value: Record<string, unknown>): Record<string, unknown> {
  const cleaned = stripUndefinedDeep(value);
  if (cleaned === undefined || cleaned === null || typeof cleaned !== "object") {
    return {};
  }
  return cleaned as Record<string, unknown>;
}
